import { useCallback, useEffect, useMemo } from 'react';
import { usePaginationParams } from '@common-fe/common-fe';

import { ALL_OPTION, OrganizationTypes } from '@/common/constants';
import routes from '@/common/routes';
import { useCurrentOrganization, useHistory } from '@/modules/core/hooks';
import { SelectedOrganization } from '@/modules/employer/components/BankingBalance/types';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { checkIsAllOptionSelected, stateAllToggling } from '@/utils/handlers/dropdownStateAllToggling';
import { useFilters } from '@/utils/hooks/useFilters';

const DEFAULT_STATUSES = [
  EmployerStatus.Active,
  EmployerStatus.PendingTermination,
  EmployerStatus.TerminationInProgress,
  EmployerStatus.PendingReactivation,
];

export const STATUS_OPTIONS = [
  ALL_OPTION,
  { key: EmployerStatus.Active, title: 'Active' },
  { key: EmployerStatus.PendingTermination, title: 'Pending termination' },
  { key: EmployerStatus.TerminationInProgress, title: 'Termination in progress' },
  { key: EmployerStatus.PendingReactivation, title: 'Pending reactivation' },
  { key: EmployerStatus.Terminated, title: 'Terminated' },
];

const useBankingBalanceList = () => {
  const { observingOrganization } = useCurrentOrganization();
  const history = useHistory();
  const { page, setPage, perPage, setPerPage } = usePaginationParams(20);

  const { filtersValues, setFilterValue } = useFilters<{
    balanceStatuses: string[];
    selectedOrgId: string;
    selectedOrgName: string;
    selectedOrgType: string;
  }>({
    balanceStatuses: DEFAULT_STATUSES,
    selectedOrgId: '',
    selectedOrgName: '',
    selectedOrgType: '',
  }, { autoClear: true });

  const selectedOrg = useMemo<SelectedOrganization | null>(() => {
    if (!filtersValues.selectedOrgId) return null;
    return {
      id: filtersValues.selectedOrgId,
      name: filtersValues.selectedOrgName || '',
      type: (filtersValues.selectedOrgType as OrganizationTypes) || OrganizationTypes.partner,
    };
  }, [filtersValues.selectedOrgId, filtersValues.selectedOrgName, filtersValues.selectedOrgType]);

  const statusesAfterToggling = useMemo(() => stateAllToggling(filtersValues.balanceStatuses || []), [filtersValues.balanceStatuses]);
  const isAllStatusesSelected = useMemo(() => checkIsAllOptionSelected(filtersValues.balanceStatuses || []), [filtersValues.balanceStatuses]);

  const queryStatuses = useMemo(
    () => isAllStatusesSelected ? DEFAULT_STATUSES : statusesAfterToggling as string[],
    [isAllStatusesSelected, statusesAfterToggling]
  );

  const handleOrgChange = useCallback((org: SelectedOrganization | null) => {
    if (org?.type === OrganizationTypes.subsidiary) {
      history.push(routes.BANKING_BALANCE_ORG_DETAILS(org.id));
      return;
    }
    setFilterValue.selectedOrgId(org?.id || '');
    setFilterValue.selectedOrgName(org?.name || '');
    setFilterValue.selectedOrgType(org?.type || '');
    setPage(1);
  }, [setFilterValue, setPage, history]);

  const handleShowChildren = useCallback((orgId: number, orgName: string, orgType: OrganizationTypes) => {
    setFilterValue.selectedOrgId(String(orgId));
    setFilterValue.selectedOrgName(orgName);
    setFilterValue.selectedOrgType(orgType);
    setPage(1);
  }, [setFilterValue, setPage]);

  const handleViewOrgDetails = useCallback((orgId: number) => {
    history.push(routes.BANKING_BALANCE_ORG_DETAILS(String(orgId)));
  }, [history]);

  const handleViewSelectedOrgDetails = useCallback(() => {
    if (selectedOrg?.id) {
      history.push(routes.BANKING_BALANCE_ORG_DETAILS(selectedOrg.id));
    }
  }, [history, selectedOrg?.id]);

  const displayOrg = useMemo(
    () => {
      if (selectedOrg) return selectedOrg;
      if (filtersValues.selectedOrgId) {
        return { ...observingOrganization, id: filtersValues.selectedOrgId };
      }
      return observingOrganization;
    },
    [observingOrganization, selectedOrg, filtersValues.selectedOrgId]
  );

  const isChildrenLeafLevel = useMemo(
    () => displayOrg?.type === OrganizationTypes.employer
      || displayOrg?.type === OrganizationTypes.company,
    [displayOrg?.type]
  );

  useEffect(() => {
    if (observingOrganization.type === OrganizationTypes.subsidiary) {
      history.push(routes.BANKING_BALANCE_ORG_DETAILS(observingOrganization.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observingOrganization.id, observingOrganization.type]);

  return {
    displayOrg,
    isChildrenLeafLevel,
    page,
    setPage,
    perPage,
    setPerPage,
    statuses: statusesAfterToggling,
    queryStatuses,
    setStatuses: setFilterValue.balanceStatuses,
    selectedOrg,
    handleOrgChange,
    handleShowChildren,
    handleViewOrgDetails,
    handleViewSelectedOrgDetails,
  };
};

export default useBankingBalanceList;
